/**
 * Combobox: lista filtrable para catálogos largos (zonas horarias IANA, sedes, países).
 * Radix no tiene primitiva de combobox: se compone Popover + patrón ARIA 1.2 `combobox` con
 * `listbox` (teclas ↑ ↓ Inicio Fin Enter Escape) sin dependencias adicionales.
 */
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronDown } from 'lucide-react';
import { useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';

import { t } from '../../i18n';
import { cn } from '../../lib/cn';

export interface ComboboxOption {
  value: string;
  label: string;
  /** Texto adicional para la búsqueda (p. ej. alias de una zona horaria). */
  keywords?: string;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  id?: string;
  disabled?: boolean;
  invalid?: boolean;
  'aria-describedby'?: string;
  'aria-label'?: string;
  'aria-required'?: true;
  className?: string;
}

function normalize(text: string): string {
  return text.toLocaleLowerCase('es-PE').normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  id,
  disabled,
  invalid,
  className,
  ...aria
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(() => {
    const needle = normalize(query.trim());
    if (needle.length === 0) {
      return options;
    }
    return options.filter((option) =>
      normalize(`${option.label} ${option.keywords ?? ''}`).includes(needle),
    );
  }, [options, query]);

  const selected = options.find((option) => option.value === value);

  function commit(index: number) {
    const option = filtered[index];
    if (!option) {
      return;
    }
    onValueChange(option.value);
    setOpen(false);
    setQuery('');
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((current) => Math.min(current + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(Math.max(filtered.length - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      commit(activeIndex);
    }
  }

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        setQuery('');
        setActiveIndex(0);
      }}
    >
      <PopoverPrimitive.Trigger
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-invalid={invalid ? true : undefined}
        disabled={disabled}
        className={cn(
          'inline-flex h-fm-control-sm w-full items-center justify-between gap-fm-2 rounded-fm-md',
          'border border-fm-border bg-fm-surface px-fm-3 text-fm-sm text-fm-text',
          'transition-colors duration-fm-fast ease-fm-standard motion-reduce:transition-none',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus',
          'aria-[invalid=true]:border-fm-danger disabled:opacity-45',
          className,
        )}
        {...aria}
      >
        <span className={cn('truncate', !selected && 'text-fm-text-disabled')}>
          {selected?.label ?? placeholder ?? t('common.buttons.search')}
        </span>
        <ChevronDown size={16} aria-hidden="true" className="text-fm-text-secondary" />
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          sideOffset={4}
          className="z-50 w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-fm-md border border-fm-border bg-fm-surface shadow-fm-2"
        >
          <div className="border-b border-fm-border p-fm-2">
            <input
              autoFocus
              role="combobox"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder ?? t('common.buttons.search')}
              aria-label={searchPlaceholder ?? t('common.buttons.search')}
              aria-expanded={open}
              aria-controls={listId}
              aria-activedescendant={filtered[activeIndex] ? `${listId}-option-${activeIndex}` : undefined}
              className="h-fm-control-sm w-full rounded-fm-sm bg-fm-surface px-fm-2 text-fm-sm text-fm-text placeholder:text-fm-text-disabled focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus"
            />
          </div>

          <ul
            id={listId}
            ref={listRef}
            role="listbox"
            className="max-h-[280px] overflow-y-auto p-fm-1"
          >
            {filtered.length === 0 ? (
              <li className="px-fm-3 py-fm-2 text-fm-sm text-fm-text-secondary">
                {emptyMessage ?? t('common.states.noResults')}
              </li>
            ) : (
              filtered.map((option, index) => (
                <li key={option.value}>
                  <button
                    id={`${listId}-option-${index}`}
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => commit(index)}
                    className={cn(
                      'flex h-fm-touch w-full items-center gap-fm-2 rounded-fm-sm px-fm-3 text-left text-fm-sm text-fm-text',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fm-focus',
                      index === activeIndex && 'bg-fm-bg-sunken',
                    )}
                  >
                    <Check
                      size={16}
                      aria-hidden="true"
                      className={cn(
                        'text-fm-primary-text',
                        option.value === value ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className="truncate">{option.label}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
